import LazoUserService from "@/services/LazoUserService";
import { useMutation, useQuery } from "@tanstack/react-query";

export const LAZO_USER_QUERY_KEY = "lazo-user";

// ========== QUERIES ==========

export function useLazoUserByCoreUser() {
  return useQuery({
    queryKey: [LAZO_USER_QUERY_KEY, "by-core-user"],
    queryFn: () => LazoUserService.getByCoreUser().then((r) => r.getContentOrThrowError()),
    meta: {
      errorMessage: "Error obtaining user",
    },
  });
}

export function useLazoUserByCoreUserExternalId(externalId: string) {
  return useQuery({
    queryKey: [LAZO_USER_QUERY_KEY, "by-core-user-external-id", externalId],
    queryFn: () => LazoUserService.getByCoreUserExternalId(externalId).then((r) => r.getContentOrThrowError()),
    enabled: !!externalId,
    meta: {
      errorMessage: "Error obtaining user",
    },
  });
}

export function useLazoUserByExternalId(externalId: string) {
  return useQuery({
    queryKey: [LAZO_USER_QUERY_KEY, "by-external-id", externalId],
    queryFn: () => LazoUserService.getByExternalId(externalId).then((r) => r.getContentOrThrowError()),
    enabled: !!externalId,
    meta: {
      errorMessage: "Error obtaining user information",
    },
  });
}

export function useLazoEmployees() {
  return useQuery({
    queryKey: [LAZO_USER_QUERY_KEY, "lazo-employees"],
    queryFn: () => LazoUserService.getLazoEmployees().then((r) => r.getContentOrThrowError()),
    select: (data) => data.sort((a, b) => a.user.firstName.localeCompare(b.user.firstName)),
    meta: {
      errorMessage: "Error obtaining preparers and reviewers",
    },
  });
}

export function useKams() {
  return useQuery({
    queryKey: [LAZO_USER_QUERY_KEY, "kams"],
    queryFn: () => LazoUserService.getAllKam().then((r) => r.getContentOrThrowError()),
    meta: {
      errorMessage: "Error obtaining KAMs",
    },
  });
}

export function useAllByTenantExternalId(tenantExternalId: string) {
  return useQuery({
    queryKey: [LAZO_USER_QUERY_KEY, "by-tenant", tenantExternalId],
    queryFn: () => LazoUserService.getAllByTenantExternalId(tenantExternalId).then((r) => r.getContentOrThrowError()),
    select: (data) => data.sort((a, b) => a.user.firstName.localeCompare(b.user.firstName)),
    enabled: !!tenantExternalId,
    placeholderData: [],
    meta: {
      errorMessage: "Error obtaining company client users",
    },
  });
}

export function useLazoUserLanguage(userExternalId: string) {
  return useQuery({
    queryKey: [LAZO_USER_QUERY_KEY, userExternalId, "language"],
    queryFn: () => LazoUserService.getLanguage(userExternalId).then((r) => r.getContentOrThrowError()),
    enabled: !!userExternalId,
    meta: {
      errorMessage: "Error obtaining user language",
    },
  });
}

// ========== MUTATIONS ==========

export function useUpdateLanguage(userExternalId: string) {
  return useMutation({
    mutationFn: (language: string) => LazoUserService.updateLanguage(userExternalId, language).then((r) => r.getContentOrThrowError()),
    mutationKey: [LAZO_USER_QUERY_KEY, userExternalId, "language"],
    meta: {
      errorMessage: "Error updating user language",
    },
  });
}
